import {
  IBay40sAllowance,
  MIN_PAIRED_BAY_LENGTH,
  TReducerISlotDataActions,
} from "@tedivo/tedivo-bay-grid-pure";
import { IFeaturesAllowed, TContainerLengths } from "open-vessel-definition";
import {
  ILegendOptions,
  getSlotCapabilitiesLegendOptions,
} from "@tedivo/tedivo-bay-grid-core";

import EditToolsEnum from "../../../types/EditToolsEnum";
import { IOptionChanged } from "../../../../../../../typings";
import { IToolsState } from "../../../types/IToolsState";
import SlButton from "@shoelace-style/shoelace/dist/components/button/button";
import SlCheckbox from "@shoelace-style/shoelace/dist/components/checkbox/checkbox";
import SlRadio from "@shoelace-style/shoelace/dist/components/radio/radio";
import SlRadioGroup from "@shoelace-style/shoelace/dist/components/radio-group/radio-group";
import SlSwitch from "@shoelace-style/shoelace/dist/components/switch/switch";
import { createMultiEditButton } from "@tedivo/tedivo-form";
import { getTranslation } from "../../../../../../app/i18/i18tn";
import globalStore from "../../../../../../app/stores/globalStore";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";

/**
 * Generates the menu options for the Bay-Edit tools
 * @returns HTMLNode
 */
export class BayEditTopTools extends EventTarget {
  state: IToolsState = {
    activeTool: EditToolsEnum.ADD_MANY,
    tools: {
      [EditToolsEnum.ADD_MANY]: [],
      [EditToolsEnum.ADD_REMOVE_SINGLE]: [],
      [EditToolsEnum.CLEAR]: [],
      [EditToolsEnum.ADD_REMOVE_RESTRICTED]: [],
      [EditToolsEnum.ADD_REMOVE_SPECIAL]: [],
    },
  };

  private nodes: INodes;

  private containersLengths: Array<TContainerLengths> = [];
  private featuresAllowed: IFeaturesAllowed | undefined = undefined;
  private internalSizes40Allowance: IBay40sAllowance = { allowed: true };

  /** Whether the Bay being edited may hold 40'+ definitions. See `can40sBeDefinedInBay` */
  get sizes40Allowance(): IBay40sAllowance {
    return this.internalSizes40Allowance;
  }
  set sizes40Allowance(v: IBay40sAllowance) {
    this.internalSizes40Allowance = v;
    this.refreshSizes40Disabled();
  }

  get mirrorSlotsWhenDefining() {
    return this.nodes.chkMirrorSlotsWhenDefining?.checked || false;
  }

  get emphasizedValue() {
    return this.nodes.chkEmphasizePairedSlots?.checked || false;
  }

  get emphasizeCheckboxDisabled() {
    return this.nodes.chkEmphasizePairedSlots?.disabled || false;
  }
  set emphasizeCheckboxDisabled(v) {
    if (this.nodes.chkEmphasizePairedSlots) {
      this.nodes.chkEmphasizePairedSlots.disabled = v;
    }
  }

  get mainNode() {
    return this.nodes.mainNode;
  }

  private buildHtml(): INodes {
    const mainNode = document.createElement("div");
    mainNode.setAttribute(
      "data-set-id",
      Math.round(Math.random() * 10000).toString(),
    );

    const toolsDiv = document.createElement("div");
    toolsDiv.className = "edit-tools";

    // Main buttons
    const label = document.createElement("label");
    label.innerHTML = getTranslation("view:edit.tools.clickAction");

    // flex-holder
    const flexHolderTop = document.createElement("div");
    flexHolderTop.className = "flex-holder-top";

    // Add the buttons to main
    const buttonGroup = document.createElement("sl-radio-group");
    buttonGroup.addEventListener("click", this.onClickMenu, false);

    // Emphasize paired slots
    const chkEmphasizePairedSlots = document.createElement("sl-switch");
    chkEmphasizePairedSlots.innerHTML = getTranslation(
      "view:edit.helpers.highlightPairedSlots",
    );
    chkEmphasizePairedSlots.addEventListener(
      "sl-change",
      (ev) => {
        const isChecked = (ev.target as SlSwitch).checked;
        this.dispatchEvent(
          new CustomEvent<ICustomEventEmphasizeCheckbox>(
            "emphasizePairedSlotsChanged",
            { detail: { isChecked } },
          ),
        );
      },
      false,
    );

    // Mirror slots definition
    const chkMirrorSlotsWhenDefining = document.createElement("sl-switch");
    chkMirrorSlotsWhenDefining.innerHTML = getTranslation(
      "view:edit.helpers.mirrorSlotsDefinition",
    );
    chkMirrorSlotsWhenDefining.checked = true;
    chkMirrorSlotsWhenDefining.addEventListener(
      "sl-change",
      (ev) => {
        const isChecked = (ev.target as SlSwitch).checked;
        this.dispatchEvent(
          new CustomEvent<ICustomEventEmphasizeCheckbox>(
            "mirrorSlotsDefinitionChanged",
            { detail: { isChecked } },
          ),
        );
      },
      false,
    );

    // Helpers

    const helpersHolder = document.createElement("div");
    helpersHolder.className = "helpers-holder";
    const helpersMainButton = createMultiEditButton({
      id: `helpersCGs`,
      title: getTranslation("view:edit.cgHelpers.title"),
      size: "small",
      variant: "primary",
      showTitleAsTooltip: false,
      iconName: "command",
      options: [
        {
          value: "copyFromBay",
          name: getTranslation("view:edit.helpers.copyFromBay.title"),
          action: () => {
            this.dispatchEvent(new CustomEvent("copyFromBay"));
          },
        },
        {
          value: "copyToBays",
          name: getTranslation("view:edit.helpers.copyToBays.title"),
          action: () => {
            this.dispatchEvent(new CustomEvent("copyToBays"));
          },
        },
        {
          value: "remapTiers",
          name: getTranslation("view:edit.helpers.remapTiers.title"),
          action: () => {
            this.dispatchEvent(new CustomEvent("remapTiers"));
          },
        },
        {
          value: "move40Definitions",
          name: getTranslation("view:edit.helpers.move40Definitions.title"),
          action: () => {
            this.dispatchEvent(new CustomEvent("move40Definitions"));
          },
        },
      ],
    });
    helpersHolder.appendChild(helpersMainButton);

    // Options for each button
    const toolsOptionsDiv = document.createElement("div");
    toolsOptionsDiv.className = "tools-options";

    flexHolderTop.appendChild(buttonGroup);
    flexHolderTop.appendChild(helpersHolder);
    flexHolderTop.appendChild(chkMirrorSlotsWhenDefining);
    flexHolderTop.appendChild(chkEmphasizePairedSlots);

    return {
      mainNode,
      toolsDiv,
      label,
      flexHolderTop,
      buttonGroup,
      chkEmphasizePairedSlots,
      chkMirrorSlotsWhenDefining,
      helpersHolder,
      toolsOptionsDiv,
    };
  }

  public updateToolsOptions(
    containersLengths: Array<TContainerLengths>,
    featuresAllowed: IFeaturesAllowed | undefined,
    sizes40Allowance: IBay40sAllowance = { allowed: true },
  ) {
    const buttonGroup = this.nodes.buttonGroup;
    const toolsOptionsDiv = this.nodes.toolsOptionsDiv;

    this.containersLengths = containersLengths;
    this.featuresAllowed = featuresAllowed;
    this.internalSizes40Allowance = sizes40Allowance;

    removeChildren(buttonGroup);
    removeChildren(toolsOptionsDiv);

    getMenuOptions(
      this.containersLengths || [],
      this.featuresAllowed,
      globalStore.resolvedColorMode === "dark",
    ).forEach((option, idx) => {
      const button = document.createElement("sl-radio-button");
      button.value = String(option.id);
      button.pill = true;
      button.innerHTML = getTranslation(option.label);
      buttonGroup.appendChild(button);

      if (idx === 0) {
        this.state.activeTool = option.id;
        buttonGroup.value = String(option.id);
      }

      this.state.tools[option.id] = [];

      const d = document.createElement("div");
      d.className = "tools-options-inst";
      d.dataset.value = String(option.id);
      toolsOptionsDiv.appendChild(d);
      d.style.display = idx === 0 ? "flex" : "none";

      // Disabled options swallow their own "sl-change", so the explanation has to
      // be hooked on the way down
      d.addEventListener("click", this.onClickBlockedSize, true);

      // Add the toolsOptions to each option
      if (option.optionsControl) {
        const parent =
          option.optionsControl === "sl-radio"
            ? document.createElement("sl-radio-group")
            : d;

        if (option.getOptions) {
          const toolsOptions = option.getOptions();
          toolsOptions.forEach((v) => {
            this.createSubOption(option, v, this.state, parent);
          });
        }

        if (option.optionsControl === "sl-radio") {
          d.appendChild(parent);
          // sl-radio-group uses "value" (v2.0.82), not individual sl-radios to assign value
          if (option.initial)
            (parent as SlRadioGroup).value = option.initial[0];
          // same version needs listener
          parent.addEventListener(
            "sl-change",
            () => {
              const state = this.state;
              const menuId = option.id;
              const value = (parent as SlRadioGroup)
                .value as TReducerISlotDataActions;
              if (state.tools[menuId][0] === value) return;
              state.tools[option.id] = [value];

              this.dispatchEvent(
                new CustomEvent<IOptionChanged>("optionChanged", {
                  detail: { menuId, optionId: value, state },
                }),
              );
            },
            false,
          );
        }
      }
    });
  }

  constructor() {
    super();

    this.nodes = this.buildHtml();

    const {
      mainNode,
      toolsDiv,
      label,
      flexHolderTop,
      chkEmphasizePairedSlots,
      toolsOptionsDiv,
    } = this.nodes;

    toolsDiv.appendChild(label);
    toolsDiv.appendChild(flexHolderTop);
    toolsDiv.appendChild(toolsOptionsDiv);

    // Start with emphasized slots of pairedBay
    chkEmphasizePairedSlots.checked = true;
    chkEmphasizePairedSlots.dispatchEvent(new Event("sl-change"));

    mainNode.appendChild(toolsDiv);
  }

  private onClickMenu = (ev: Event) => {
    const target = ev.target as SlButton;
    const value = target.value;

    this.state.activeTool = Number(value);

    // hack
    document
      .querySelectorAll<HTMLElement>(".tools-options-inst")
      .forEach((d) => {
        d.style.display = d.dataset.value === value ? "flex" : "none";
      });
  };

  private onClickOption = (
    ev: Event,
    menuId: EditToolsEnum,
    optionId: TReducerISlotDataActions,
    optionsControl: "sl-checkbox" | "sl-radio",
  ) => {
    const state = this.state;

    if (optionsControl === "sl-checkbox") {
      if (state.tools[menuId].indexOf(optionId) >= 0) {
        state.tools[menuId] = state.tools[menuId].filter((v) => v !== optionId);
      } else {
        state.tools[menuId].push(optionId);
      }
    } else {
      if (state.tools[menuId][0] === optionId) return;
      state.tools[menuId] = [optionId];
    }

    this.dispatchEvent(
      new CustomEvent<IOptionChanged>("optionChanged", {
        detail: { menuId, optionId, state },
      }),
    );
  };

  private createSubOption(
    option: IMenuOption,
    v: ILegendOptions,
    state: IToolsState,
    parent: HTMLElement,
  ) {
    if (option === undefined || option.optionsControl === undefined) return;
    const optionsControl = option.optionsControl;

    const ctrl = document.createElement(optionsControl);
    ctrl.appendChild(
      createSubLabel(
        `${getTranslation(v.labelKey)} ${v.labelKey.indexOf("misc") > 0
          ? `- ${v.labelKey.split(".").pop()}`
          : ""
        }`,
        v.bgColor,
      ),
    );
    ctrl.value = v.id;
    ctrl.dataset.menuId = String(option.id);

    const needsPairedBay = optionNeedsPairedBay(v.id);
    if (needsPairedBay) ctrl.dataset.size40 = "1";

    const isBlocked = needsPairedBay && !this.internalSizes40Allowance.allowed;
    if (isBlocked) ctrl.disabled = true;

    // A blocked option must stay out of the state too: `initial` would otherwise
    // push it in regardless of the control, and `modifyCellsWithActions` apply it
    if (!isBlocked && option.initial !== undefined && option.initial.indexOf(v.id) >= 0) {
      if (optionsControl === "sl-checkbox") ctrl.checked = true;
      state.tools[option.id].push(v.id);
    }

    if (optionsControl === "sl-checkbox")
      ctrl.addEventListener(
        "sl-change",
        (ev) => this.onClickOption(ev, option.id, v.id, optionsControl),
        false,
      );

    parent.appendChild(ctrl);
  }

  /** Re-applies the current allowance to the options already on screen */
  private refreshSizes40Disabled() {
    const allowed = this.internalSizes40Allowance.allowed;
    const state = this.state;

    this.nodes.toolsOptionsDiv
      .querySelectorAll<SlCheckbox | SlRadio>("[data-size40]")
      .forEach((ctrl) => {
        ctrl.disabled = !allowed;
        if (allowed) return;

        const menuId = Number(ctrl.dataset.menuId) as EditToolsEnum;
        const optionId = ctrl.value as TReducerISlotDataActions;

        state.tools[menuId] = state.tools[menuId].filter((v) => v !== optionId);
        if (ctrl.tagName === "SL-CHECKBOX") (ctrl as SlCheckbox).checked = false;
      });

    if (allowed) return;

    // A radio group still pointing at a blocked option needs a new selection
    this.nodes.toolsOptionsDiv
      .querySelectorAll<SlRadioGroup>("sl-radio-group")
      .forEach((group) => {
        const radios = Array.from(group.querySelectorAll<SlRadio>("sl-radio"));
        const current = radios.find((r) => r.value === group.value);
        if (current !== undefined && !current.disabled) return;

        const firstEnabled = radios.find((r) => !r.disabled);
        if (firstEnabled === undefined) return;

        group.value = firstEnabled.value;

        const menuId = Number(firstEnabled.dataset.menuId) as EditToolsEnum;
        state.tools[menuId] = [firstEnabled.value as TReducerISlotDataActions];
      });
  }

  private onClickBlockedSize = (ev: Event) => {
    if (this.internalSizes40Allowance.allowed) return;

    const hitBlocked = ev
      .composedPath()
      .some(
        (node) =>
          node instanceof HTMLElement &&
          node.dataset.size40 === "1" &&
          (node as SlCheckbox | SlRadio).disabled,
      );

    if (!hitBlocked) return;

    ev.preventDefault();
    ev.stopPropagation();

    this.dispatchEvent(new CustomEvent("blocked40sClicked"));
  };
}

const SIZE_OPTION_RE = /^ADD_SIZE_(\d+)(_CONEREQ)?$/;

/** Sizes of 40' and over span two bays, so they need a paired one */
function optionNeedsPairedBay(id: TReducerISlotDataActions): boolean {
  const match = SIZE_OPTION_RE.exec(id);
  return match !== null && Number(match[1]) >= MIN_PAIRED_BAY_LENGTH;
}

function createSubLabel(label: string, bgColor: string): HTMLElement {
  const sp = document.createElement("span");
  const em = document.createElement("em");
  sp.innerHTML = label;
  sp.style.position = "relative";
  em.style.backgroundColor = bgColor;
  em.style.display = "inline-block";
  em.style.position = "absolute";
  em.style.width = "100%";
  em.style.height = "2px";
  em.style.bottom = "-1px";
  em.style.left = "0";
  em.style.borderRadius = "2px";
  sp.appendChild(em);
  sp.style.pointerEvents = "none";
  return sp;
}

function getMenuOptions(
  availableLengths: TContainerLengths[],
  featuresAllowed = {} as IFeaturesAllowed,
  isDarkMode = false,
): IMenuOption[] {
  const options = [
    {
      id: EditToolsEnum.ADD_MANY,
      label: "view:edit.tools.addMany",
      optionsControl: "sl-checkbox",
      initial: ["ADD_SIZE_20", "ADD_SIZE_40"],
      getOptions: () =>
        getSlotCapabilitiesLegendOptions(
          availableLengths,
          ["sizes", "misc"],
          featuresAllowed,
          isDarkMode,
        ),
    },
    {
      id: EditToolsEnum.ADD_REMOVE_SINGLE,
      label: "view:edit.tools.addRemoveSingle",
      optionsControl: "sl-radio",
      initial: ["ADD_SIZE_20"],
      getOptions: () =>
        getSlotCapabilitiesLegendOptions(
          availableLengths,
          ["sizes", "misc"],
          featuresAllowed,
          isDarkMode,
        ),
    },
    featuresAllowed.slotConeRequired
      ? {
        id: EditToolsEnum.ADD_REMOVE_SPECIAL,
        label: "view:edit.tools.special",
        optionsControl: "sl-radio",
        initial: ["ADD_SIZE_20_CONEREQ"],
        getOptions: () =>
          getSlotCapabilitiesLegendOptions(
            availableLengths,
            ["sizesWithCones"],
            featuresAllowed,
            isDarkMode,
          ),
      }
      : undefined,
    {
      id: EditToolsEnum.ADD_REMOVE_RESTRICTED,
      label: "view:edit.tools.restricted",
    },
    {
      id: EditToolsEnum.CLEAR,
      label: "view:edit.tools.clear",
    },
  ].filter(Boolean);

  return options as IMenuOption[];
}

interface IMenuOption {
  id: EditToolsEnum;
  label: string;
  optionsControl?: "sl-radio" | "sl-checkbox";
  initial?: TReducerISlotDataActions[];
  getOptions?: () => ILegendOptions[];
}

interface INodes {
  mainNode: HTMLDivElement;
  toolsDiv: HTMLDivElement;
  label: HTMLLabelElement;
  flexHolderTop: HTMLDivElement;
  buttonGroup: SlRadioGroup;
  chkEmphasizePairedSlots: SlSwitch;
  chkMirrorSlotsWhenDefining: SlSwitch;
  helpersHolder: HTMLDivElement;
  toolsOptionsDiv: HTMLDivElement;
}

export interface ICustomEventEmphasizeCheckbox {
  isChecked: boolean;
}
